import { CoreButton, CoreFocusTrap, CoreIconButton } from "../core";
import ThemeWrapper from "../theme";
import styles from "./before-quiz.module.css";
import i18nPackage from "../i18n/i18n";
import { Button, Tooltip, Typography } from "@mui/material";
import BackButtonIcon from "../images/x.svg?react";
import { BeforeQuizStepper, BeforeQuizStepperProps } from "./components/before-quiz-stepper";
import React, { useEffect, useRef, useState } from "react";
import Timer from "../images/timer.svg?react";
import WelcomeStep, { WelcomeStepProps } from "./steps/welcome";
import { Trans } from "react-i18next";
import { useOverflow } from "../hooks";
import ScrollDownIcon from "../images/scroll-down.svg?react";
import { ConditionalWrapper } from "../wrapper";

export type StepKey =
	| "payment"
	| "systemCheck"
	| "assessmentSetup"
	| "deviceSetup"
	| "roomScan"
	| "verifyIdentity"
	| "review";

export type AllSubstepKeys =
	| SystemCheckKey
	| AssessmentSetupKey
	| DeviceSetupKey
	| RoomScanKey
	| VerifyIdentityKey
	| AppliedLimitsKey;

export type SystemCheckKey = "systemResources" | "connectionCheck" | "enableGeofence";

export type AssessmentSetupKey =
	| "managedGuestSessionCheck"
	| "incognitoCheck"
	| "oneScreenEnforcementMode"
	| "closeOpenTabs"
	| "blockVirtualMachines"
	| "recordApplicationsActivity"
	| "restrictDevices"
	| "applicationBlockMode"
	| "extensionBlockMode"
	| "detectSidePanel";

export type DeviceSetupKey = "videoRecordingMode" | "audioRecordingMode" | "recordScreen";

export type RoomScanKey = "scanRoom";

export type VerifyIdentityKey = "idCard" | "faceVerification" | "signature" | "review";

export type VerifyIdentityKeyNoReview = Exclude<VerifyIdentityKey, "review">;

export type AppliedLimitsKey =
	| "disableRightClick"
	| "disableNewTab"
	| "disableClipboardMode"
	| "closeOpenTabs"
	| "recordWebTraffic"
	| "disableDownloadFiles"
	| "disablePrinting"
	| "hideOtherWindows";

type BeforeQuizPageProps = {
	footerButtons: Pick<BeforeQuizFooterProps, "backButton" | "nextButton">;
	beforeQuizStepper: Omit<BeforeQuizStepperProps, "isCompatibilityCheck" | "disabled">;
	stepContent: React.ReactNode;
	onClose: () => void;
	isCompatibilityCheck: boolean;
	liveTime?: LiveTimeProps;
	quizInfo: Pick<WelcomeStepProps, "courseName" | "quizName">;
};

// THIS HANDLES THE WELCOME PAGE
export function BeforeQuizPage({
	footerButtons,
	beforeQuizStepper,
	stepContent,
	onClose,
	isCompatibilityCheck,
	liveTime,
	quizInfo,
}: BeforeQuizPageProps) {
	const [showWelcomePage, setShowWelcomePage] = useState<boolean>(true);
	const liveComponent = liveTime ? <LiveTime secondsRemaining={liveTime.secondsRemaining} /> : undefined;
	// capture the actual DOM element used for overflow checking
	const [stepContentElement, setStepContentElement] = useState<HTMLDivElement | null>(null);
	const [showScrollHint, setShowScrollHint] = useState<boolean>(false);

	// Attached to the outermost container. Used for focus trap
	const containerRef = useRef<HTMLDivElement | null>(null);

	// Scrolls caused by clicking "Scroll down"
	const ignoreNextScrollEvent = useRef<boolean>(false);
	const [attachOverflowRef, stepContentOverflow] = useOverflow();

	useEffect(() => {
		if (!stepContentElement) return;

		const handleScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = stepContentElement;

			// If user is at (or near) bottom → hide hint
			const atBottom = scrollTop + clientHeight >= scrollHeight - 20;

			if (atBottom) {
				setShowScrollHint(false);
				return;
			}
			if (!ignoreNextScrollEvent.current) {
				setShowScrollHint(true); // only show if user scrolled manually
			}
		};

		stepContentElement.addEventListener("scroll", handleScroll);

		return () => {
			stepContentElement.removeEventListener("scroll", handleScroll);
		};
	}, [stepContentElement]);

	// Attach element to both overflow hook and local scrolling ref
	const handleStepContentRef = (el: HTMLDivElement | null) => {
		setStepContentElement(el);
		attachOverflowRef(el);
	};

	const handleScrollDown = () => {
		if (!stepContentElement) return;

		ignoreNextScrollEvent.current = true; // block scroll events
		setShowScrollHint(false);

		stepContentElement.scrollTo({
			top: stepContentElement.scrollHeight,
			behavior: "smooth",
		});

		// Allow scroll events again AFTER smooth scroll is done
		setTimeout(() => {
			ignoreNextScrollEvent.current = false;
		}, 500); // matches browser smooth scroll duration
	};

	// FOCUS TRAP
	const getFocusables = () => {
		if (!containerRef.current) return [];

		return (
			Array.from(
				containerRef.current.querySelectorAll<HTMLElement>(
					`button, [href], input, select, textarea, iframe, [tabindex]:not([tabindex="-1"]):not([data-core-focus-trap])`,
				),
			)
				// remove disabled items
				.filter((el) => !el.hasAttribute("disabled"))
				// remove tabIndex=-1
				.filter((el) => el.tabIndex !== -1)
				// remove hidden / display:none / visibility:hidden
				.filter((el) => el.offsetParent !== null)
				// bounding box check
				.filter((el) => el.offsetWidth > 0 || el.offsetHeight > 0)
		);
	};

	function getFirstElement() {
		return getFocusables()[0];
	}

	function getLastElement() {
		const list = getFocusables();
		return list[list.length - 1];
	}

	const focusLastElement = () => {
		const last = getLastElement();

		if (last instanceof HTMLIFrameElement && last.contentDocument) {
			// Handles tab + shifting for iframes (like the Stripe Payment Iframe)
			try {
				const inner = last.contentDocument.querySelectorAll<HTMLElement>(
					`button, [href], input, select, textarea, iframe, [tabindex]:not([tabindex="-1"])`,
				);
				if (inner.length > 0) {
					inner[inner.length - 1].focus();
					return;
				}
			} catch (e) {} // Ignore
		}

		last?.focus();
	};

	return (
		<ThemeWrapper>
			<div
				className={`${styles.beforeQuizContainer} ${!isCompatibilityCheck ? styles.beforeQuizModal : ""}`}
				ref={containerRef}
				role="dialog"
				aria-modal="true"
			>
				<CoreFocusTrap data-core-focus-trap onFocus={focusLastElement} />
				<div className={styles.closeButtonWrapper}>
					<CoreIconButton variant="plain" onClick={onClose} className={styles.closeButton}>
						<BackButtonIcon fill="#212121" />
					</CoreIconButton>
				</div>
				{showWelcomePage && (
					<WelcomeStep
						courseName={quizInfo.courseName}
						quizName={quizInfo.quizName}
						onStart={() => setShowWelcomePage(false)}
						liveComponent={liveComponent}
						isCompatibilityCheck={isCompatibilityCheck}
					/>
				)}
				{!showWelcomePage && (
					<div className={styles.mainContentContainer}>
						<div className={styles.stepperContainer}>
							{liveComponent}
							<BeforeQuizStepper
								{...beforeQuizStepper}
								isCompatibilityCheck={isCompatibilityCheck}
								disabled={footerButtons.backButton.disabled && footerButtons.nextButton.disabled}
							/>
						</div>
						<div className={styles.stepContainer} ref={handleStepContentRef}>
							{stepContent}
						</div>
					</div>
				)}
				{/**Hide buttons on the first page */}
				{!showWelcomePage && <BeforeQuizFooter {...footerButtons} containerRef={containerRef.current} />}
				{stepContentOverflow && showScrollHint && <ScrollHint onClick={handleScrollDown} />}
				<CoreFocusTrap data-core-focus-trap onFocus={() => getFirstElement()?.focus()} />
			</div>
		</ThemeWrapper>
	);
}

function ScrollHint({ onClick }: { onClick: () => void }) {
	return (
		<Button className={styles.scrollHintButton} onClick={onClick}>
			<ScrollDownIcon />
			<Typography>{i18nPackage.t("beforeQuiz.scrollDown")}</Typography>
		</Button>
	);
}

type BeforeQuizFooterProps = {
	containerRef: HTMLElement | null; // For tooltip to anchor cuz shadow dom
	backButton: {
		text?: string;
		disabled?: boolean;
		onClick?: () => void;
		tooltip?: React.ReactNode;
	};
	nextButton: {
		text?: string;
		disabled?: boolean;
		onClick?: () => void;
		tooltip?: React.ReactNode;
	};
};

function BeforeQuizFooter({ backButton, nextButton, containerRef }: BeforeQuizFooterProps) {
	const wrapButton = (button: React.ReactNode, disabled: boolean) => (
		<span className={disabled ? styles.disabledWrapper : ""}>{button}</span>
	);

	const back = (
		<CoreButton disabled={backButton.disabled} onClick={backButton.onClick} className={styles.footerButton}>
			{backButton.text ?? BEFORE_QUIZ_BUTTON_TEXT.back}
		</CoreButton>
	);

	const next = (
		<CoreButton disabled={nextButton.disabled} onClick={nextButton.onClick} className={styles.footerButton}>
			{nextButton.text ?? BEFORE_QUIZ_BUTTON_TEXT.next}
		</CoreButton>
	);

	return (
		<div className={styles.beforeQuizFooterContainer}>
			<ConditionalWrapper
				condition={backButton.tooltip !== undefined}
				whenTrue={(children) => (
					// Wrap in span so tooltip works on disabled elements
					<Tooltip title={backButton.tooltip} PopperProps={{ container: containerRef }}>
						{wrapButton(children, !!backButton.disabled)}
					</Tooltip>
				)}
			>
				{back}
			</ConditionalWrapper>
			<ConditionalWrapper
				condition={nextButton.tooltip !== undefined}
				whenTrue={(children) => (
					<Tooltip title={nextButton.tooltip} PopperProps={{ container: containerRef }}>
						{wrapButton(children, !!nextButton.disabled)}
					</Tooltip>
				)}
			>
				{next}
			</ConditionalWrapper>
		</div>
	);
}

export function getBeforeQuizTitle(isCompatibilityCheck: boolean) {
	return isCompatibilityCheck ? i18nPackage.t("beforeQuiz.titleCompatibility") : i18nPackage.t("beforeQuiz.title");
}

type LiveTimeProps = {
	secondsRemaining: number;
};

function LiveTime({ secondsRemaining }: LiveTimeProps) {
	const { hours, minutes, seconds } = secondsToTime(secondsRemaining);
	return (
		<div className={styles.timerContainer}>
			<div className={styles.timerTitleWrapper}>
				<Timer />
				<Typography>{i18nPackage.t("beforeQuiz.timerLabel")}</Typography>
			</div>
			<Typography className={styles.timerText}>
				<Trans
					i18n={i18nPackage}
					i18nKey={"beforeQuiz.timer"}
					components={{
						b: <strong />,
					}}
					values={{
						hour: hours,
						min: minutes,
						sec: seconds,
					}}
				/>
			</Typography>
		</div>
	);
}

function secondsToTime(seconds: number) {
	const hoursTime = Math.floor(seconds / (60 * 60))
		.toString()
		.padStart(2, "0");
	const minutesTime = Math.floor((seconds % (60 * 60)) / 60)
		.toString()
		.padStart(2, "0");
	const secondsTime = Math.floor(seconds % 60)
		.toString()
		.padStart(2, "0");

	return { hours: hoursTime, minutes: minutesTime, seconds: secondsTime };
}

// I defined text here, so text is in one place
export const BEFORE_QUIZ_BUTTON_TEXT = {
	upload: i18nPackage.t("beforeQuiz.buttons.upload"),
	startAssessment: i18nPackage.t("beforeQuiz.buttons.startAssessment"),
	next: i18nPackage.t("beforeQuiz.buttons.next"),
	back: i18nPackage.t("beforeQuiz.buttons.back"),
	startCheck: i18nPackage.t("beforeQuiz.buttons.startCheck"),
	continueAnyway: i18nPackage.t("beforeQuiz.buttons.continueAnyway"),
	viewReport: i18nPackage.t("beforeQuiz.buttons.viewReport"),
	screenShare: i18nPackage.t("beforeQuiz.buttons.screenShare"),
	startRoomScan: i18nPackage.t("beforeQuiz.buttons.startRoomScan"),
};

// THIS WILL BE CLEANED UP in 4583
export function BeforeQuizPageSkeleton({ isCompatibilityCheck }: { isCompatibilityCheck: boolean }) {
	return (
		<ThemeWrapper>
			<div
				className={`${styles.beforeQuizContainer} ${!isCompatibilityCheck ? styles.beforeQuizModal : ""}`}
				style={{ justifyContent: "center", alignItems: "center" }}
			>
				<WelcomeStep showSkeleton onStart={() => {}} isCompatibilityCheck={isCompatibilityCheck} />
			</div>
		</ThemeWrapper>
	);
}
